import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { CLEAR_JOB_APPLICATIONS_CACHE, SEARCH_JOB_APPLICATIONS_PRO, SEARCH_JOB_APPLICATIONS_UPERCUT } from '@gql/jobs';
import { usePaginatedQuery } from '@hooks/usePaginatedQuery';
import { IJobApplicationDetailFilter } from '@modules/jobApplications/JobApplicationFilter';
import { useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';

/**
 * Enhanced hook to fetch job applications based on current user's role
 * Automatically selects the appropriate query (Upercut vs Pro)
 * @param query - Search query string
 * @param filter - Filter options for job applications
 * @param sortBy - Sort field name
 * @param page - Current page (0-indexed)
 * @param pageSize - Number of items per page
 */
export const useGetJobApplications = (
    query: string = '',
    filter: IJobApplicationDetailFilter = {},
    sortBy: { field: string; order: number } = { field: 'date', order: -1 },
    page: number = 0,
    pageSize: number = 10,
) => {
    const { user, isUpercut } = useAuth();
    const { isProIdRequired } = useProId();

    const isUpercutView = isUpercut && !isProIdRequired;
       const queryToUse = isUpercutView
           ? SEARCH_JOB_APPLICATIONS_UPERCUT
           : SEARCH_JOB_APPLICATIONS_PRO;
   
       const gqlFilter = useMemo(() => ({
           ...filter,
           yearsOfExperience:
               filter.yearsOfExperience?.min != null || filter.yearsOfExperience?.max != null
                   ? { min: filter.yearsOfExperience.min, max: filter.yearsOfExperience.max }
                   : undefined,
           locations: filter.locations.map(loc => ({city: loc})),
           jobOfferLocations: filter.jobOfferLocations.map(loc => ({city: loc})),
       }), [filter]);

    const { rows, totalDocs, loading, isLoadingMore, hasMore, loadMore, error, refetch } = usePaginatedQuery({
        query: queryToUse,
        variables: { query, filter: gqlFilter, sortBy },
        page,
        pageSize,
        baseLimit: 10,
        paginationMode: 'offset',
        skip: !user,
        extractResult: (data) => {
            const jobApplicationsData = isUpercutView
                ? data?.searchJobApplicationsUpercut
                : data?.searchJobApplicationsPro;
            return {
                docs: jobApplicationsData?.docs ?? [],
                meta: jobApplicationsData?.meta ?? { totalDocs: 0, page: 0, limit: pageSize, totalPages: 0, nextPage: null, prevPage: null },
                totalDocs: jobApplicationsData?.meta?.totalDocs ?? 0,
            };
        },
    });

    const [clearCacheMutation] = useMutation(CLEAR_JOB_APPLICATIONS_CACHE);

    const forceRefresh = useCallback(async (proId?: string) => {
        await clearCacheMutation({ variables: proId ? { proId } : {} });
        await refetch();
    }, [clearCacheMutation, refetch]);

    return {
        jobApplications: rows,
        totalDocs,
        totalPages: Math.ceil(totalDocs / pageSize),
        loading,
        isLoadingMore,
        hasMore,
        loadMore,
        error,
        refetch,
        forceRefresh,
        isUpercut,
        userRole: user?.role,
    };
};
